import * as FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as xlsx from 'xlsx';
import { TableColumn } from '../components/table';
import { TableUtil } from '../components/table/table.util';
import { FormUtil } from './form.util';

export class FileUtil {
  static exportPdf(
    content: Array<any>,
    tableName: string,
    cols?: Array<TableColumn> | undefined,
    orientation: 'portrait' | 'landscape' | 'l' | 'p' = 'l'
  ): void {
    const doc = new jsPDF({ orientation });
    autoTable(doc, {
      columns: cols?.map((c) => ({ dataKey: c.field, header: c.header })),
      body: content
    });
    doc.save(`${tableName}-${new Date().getTime()}.pdf`);
  }

  static exportExcel(
    content: Array<unknown>,
    tableName: string,
    cols?: Array<string>,
    extraContent?: Array<{
      tableName: string;
      content: Array<unknown>;
      cols?: Array<string>;
    }>
  ) {
    if (content?.length > 60000) {
      this.exportCSV(content, tableName, cols, extraContent);
      return;
    }
    const workbook = this.createWorkbook(
      content,
      tableName,
      cols,
      extraContent
    );
    const excelBuffer: xlsx.WorkSheet = xlsx.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });
    this.saveAs(excelBuffer as BlobPart, tableName);
  }

  static saveAs(
    buffer: BlobPart,
    fileName: string,
    extension: xlsx.BookType = 'xlsx',
    type:
      | 'data:text/csv;charset=utf-8'
      | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
  ): void {
    const data: Blob = new Blob([buffer], { type });
    FileSaver.saveAs(
      data,
      fileName + '-' + new Date().getTime() + '.' + extension
    );
  }

  private static createWorkbook(
    content: Array<unknown>,
    tableName: string,
    cols?: Array<string>,
    extraContent?: Array<{
      tableName: string;
      content: Array<unknown>;
      cols?: Array<string>;
    }>
  ): xlsx.WorkBook {
    const worksheet = this.addContentToSheet(content, cols);
    const workbook = {
      Sheets: {},
      SheetNames: [tableName.slice(0, 31)]
    };
    workbook.Sheets[tableName.slice(0, 31)] = worksheet;
    if (extraContent?.length) {
      extraContent.forEach((e) => {
        workbook.Sheets[e.tableName.slice(0, 31)] = this.addContentToSheet(
          e.content,
          e.cols
        );
        workbook.SheetNames.push(e.tableName.slice(0, 31));
      });
    }
    return workbook;
  }

  private static addContentToSheet(
    content: Array<any>,
    cols?: Array<string>
  ): xlsx.WorkSheet {
    const worksheet = xlsx.utils.book_new();
    const copy = [...content].map((c) => {
      const keys = FormUtil.objectKeysAsArray(c);
      const obj: any = {};
      for (let i = 0; i < keys.length; i++) {
        let value = TableUtil.deepGet(c, keys[i].split('.'));
        if (value && Array.isArray(value)) {
          if (!value.length) value = '';
          else if (typeof value[0] === 'string') value = value.toString();
          else value = JSON.stringify(value);
        }
        obj[keys[i]] = value;
      }
      return obj;
    });
    if (cols?.length) {
      xlsx.utils.sheet_add_aoa(worksheet, [cols]);
      xlsx.utils.sheet_add_json(worksheet, copy, {
        origin: 'A2',
        skipHeader: true
      });
    } else {
      xlsx.utils.sheet_add_json(worksheet, copy);
    }
    return worksheet;
  }

  static exportCSV(
    content: Array<unknown>,
    tableName: string,
    cols?: Array<string>,
    extraContent?: Array<{
      tableName: string;
      content: Array<unknown>;
      cols?: Array<string>;
    }>
  ): void {
    const workbook = this.createWorkbook(
      content,
      tableName,
      cols,
      extraContent
    );
    const excelBuffer: xlsx.WorkSheet = xlsx.write(workbook, {
      bookType: 'csv',
      type: 'array'
    });
    this.saveAs(
      excelBuffer as BlobPart,
      tableName,
      'csv',
      'data:text/csv;charset=utf-8'
    );
    // let csvContent = 'data:text/csv;charset=utf-8,';
    // if (!cols) cols = Object.keys(content[0]);
    // csvContent += cols.join(',') + '\r\n';
    // content.forEach(function (element) {
    //   csvContent += cols.map((c) => element[c]).join(',') + '\r\n';
    // });
    // const link = document.createElement('a');
    // link.setAttribute('href', encodeURI(csvContent).replaceAll('#', '%23'));
    // link.setAttribute(
    //   'download',
    //   tableName + '_' + new Date().getTime() + '.csv'
    // );
    // link.style.height = '0px';
    // document.body.appendChild(link);
    // link.click();
  }

  static exportXML(
    content: Array<unknown>,
    tableName: string,
    cols?: Array<string>
  ): void {
    if (!cols) cols = Object.keys(content[0]);
    let xml = '<?xml version="1.0" encoding="utf-8" standalone="yes"?>\n';
    const date = new Date();
    xml += `<CustomQuery name="${tableName}" queryDate="${date.toISOString()}">\n`;
    xml += content.reduce((result, el) => {
      return (
        result +
        `  <Entity>\n${cols.reduce(
          (r, c) => (r += `    <${c}>${el[c]}</${c}>\n`),
          ''
        )}  </Entity>\n`
      );
    }, '');
    xml += '</CustomQuery>';
    const filename = `${tableName}_${new Date().toISOString()}.xml`;
    const pom = document.createElement('a');
    const bb = new Blob([xml], { type: 'text/plain' });

    pom.setAttribute('href', window.URL.createObjectURL(bb));
    pom.setAttribute('download', filename);

    pom.dataset['downloadurl'] = ['text/plain', pom.download, pom.href].join(
      ':'
    );
    pom.draggable = true;
    pom.classList.add('dragout');
    pom.click();
  }

  static b64toBlob(
    b64Data: string,
    contentType: string,
    sliceSize = 512
  ): Blob {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  static async getBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  }
}
